import {
    CardShield as SMIMEIcon,
    FingerprintCheckCircle as LicensesIcon,
    Group as OtherIcon,
    Key as PGPIcon,
    List as QueuesIcon,
    ShieldCheck as TlsIcon,
    StatsReport as DashboardIcon,
    ViewGrid as ServicesIcon
} from "iconoir-react";
import {useCallback, useEffect, useState} from "react";
import {Card, CardGroup} from "react-bootstrap";
import Row from "react-bootstrap/Row";
import Table from "react-bootstrap/Table";
import {useTranslation} from "react-i18next";
import {Link} from "react-router-dom";
import {ApiConfig} from "../api/admin/ApiConfig";
import {
    AuthApi,
    CAApi,
    CertificateApi,
    CertificateStore,
    CipherMailFeature,
    CRLApi,
    DomainApi,
    Expired,
    License,
    LicenseApi,
    MissingKeyAlias,
    MPAMailQueueApi,
    MPAMailRepositoryApi,
    MTAApi,
    PGPKeyringApi,
    PGPKeyType,
    QuarantineApi,
    SMSApi,
    SystemApi,
    SystemService,
    TLSApi,
    UserApi,
    X509CertificateDetails
} from "../api/admin/generated";
import {PageTitle} from "../common/PageTitle";
import {ReloadButton} from "../common/ReloadButton";
import {RenderAlert} from "../common/RenderAlert";
import {useEnterpriseEditionContext} from "../hook/UseEnterpriseEditionContext";
import {useHasAllPermissions} from "../hook/UseHasAllPermissions";
import {useSpinnerStatusContext} from "../hook/UseSpinnerStatusContext";
import {DateUtils} from "../utils/DateUtils";
import {DashboardTable} from "./DashboardTable";
import {DashboardTableRow} from "./DashboardTableRow";

/*
 * REST api's for accessing the back-end
 */
const tlsApi = new TLSApi(ApiConfig.config());
const licenseApi = new LicenseApi(ApiConfig.config());
const mailQueueApi = new MPAMailQueueApi(ApiConfig.config());
const mailRepositoryApi = new MPAMailRepositoryApi(ApiConfig.config());
const mtaApi = new MTAApi(ApiConfig.config());
const smsApi = new SMSApi(ApiConfig.config());
const quarantineApi = new QuarantineApi(ApiConfig.config());
const certificateApi = new CertificateApi(ApiConfig.config());
const caApi = new CAApi(ApiConfig.config());
const crlApi = new CRLApi(ApiConfig.config());
const pgpKeyringApi = new PGPKeyringApi(ApiConfig.config());
const systemApi = new SystemApi(ApiConfig.config());
const userApi = new UserApi(ApiConfig.config());
const domainApi = new DomainApi(ApiConfig.config());
const authApi = new AuthApi(ApiConfig.config());

export const Dashboard = () =>
{
    /**
     * The application version
     */
    const [versionString, setVersionString] = useState<string | undefined>("");
    /**
     * The active TLS certificate
     */
    const [activeTLSCertificate, setActiveTLSCertificate] = useState<X509CertificateDetails | undefined>(undefined);
    /**
     * The active license
     */
    const [activeLicense, setActiveLicense] = useState<License | undefined>(undefined);
    /**
     * The number of available user licenses
     */
    const [availableUserLicenses, setAvailableUserLicenses] = useState<number | undefined>(undefined);
    /**
     * The number of items in the MPA outgoing queue
     */
    const [mpaOutgoingQueueSize, setMpaOutgoingQueueSize] = useState<number | undefined>(undefined);
    /**
     * The number of items in the MPA spool queue
     */
    const [mpaSpoolQueueSize, setMpaSpoolQueueSize] = useState<number | undefined>(undefined);
    /**
     * The number of items in the MPA error queue
     */
    const [mpaErrorQueueSize, setMpaErrorQueueSize] = useState<number | undefined>(undefined);
    /**
     * The number of items in the MTA queue
     */
    const [mtaQueueSize, setMtaQueueSize] = useState<number | undefined>(undefined);
    /**
     * The number of items in the SMS queue
     */
    const [smsQueueSize, setSmsQueueSize] = useState<number | undefined>(undefined);
    /**
     * The number of items in the Quarantine queue
     */
    const [quarantineQueueSize, setQuarantineQueueSize] = useState<number | undefined>(undefined);
    /**
     * The number of certificates from the Certificates store
     */
    const [certificateCount, setCertificateCount] = useState<number | undefined>(undefined);
    /**
     * The number of expired certificates from the Certificates store
     */
    const [expiredCertificateCount, setExpiredCertificateCount] = useState<number | undefined>(undefined);
    /**
     * The number of certificates from the Root store
     */
    const [rootCount, setRootCount] = useState<number | undefined>(undefined);
    /**
     * The number of expired certificates from the Roots store
     */
    const [expiredRootCount, setExpiredRootCount] = useState<number | undefined>(undefined);
    /**
     * The number of private keys from the Certificates store
     */
    const [privateKeysCount, setPrivateKeysCount] = useState<number | undefined>(undefined);
    /**
     * The number of pending certificate requests
     */
    const [pendingRequestsCount, setPendingRequestsCount] = useState<number | undefined>(undefined);
    /**
     * The number of CRLs
     */
    const [crlCount, setCrlCount] = useState<number | undefined>(undefined);
    /**
     * The number of PGP keys
     */
    const [pgpKeyCount, setPgpKeyCount] = useState<number | undefined>(undefined);
    /**
     * The number of expired PGP keys
     */
    const [pgpExpiredKeyCount, setPgpExpiredKeyCount] = useState<number | undefined>(undefined);
    /**
     * The number of PGP secret keys
     */
    const [pgpSecretKeyCount, setPgpSecretKeyCount] = useState<number | undefined>(undefined);
    /**
     * The number of Users
     */
    const [userCount, setUserCount] = useState<number | undefined>(undefined);
    /**
     * The number of Domains
     */
    const [domainCount, setDomainCount] = useState<number | undefined>(undefined);
    /**
     * The number of Admins
     */
    const [adminCount, setAdminCount] = useState<number | undefined>(undefined);
    /**
     * True if the portal backend is running
     */
    const [portalBackendRunning, setPortalBackendRunning] = useState<boolean | undefined>(undefined);
    /**
     * True if MTA service is running
     */
    const [mtaRunning, setMtaRunning] = useState<boolean | undefined>(undefined);
    /**
     * True if IMAP service is running
     */
    const [imapRunning, setImapRunning] = useState<boolean | undefined>(undefined);
    /**
     * True if there are system updates available
     */
    const [updatesAvailable, setUpdatesAvailable] = useState<boolean | undefined>(undefined);
    /**
     * If true, the items will be reloaded
     */
    const [forceReload, setForceReload] = useState<boolean>(false);
    /**
     * Keeps track of activities for which the spinner should be shown. If an activity is started, the counter should
     * be increased by one and after the activity
     */
    const {setSpinnerStateCounter} = useSpinnerStatusContext();
    /**
     * i18next translation hook
     */
    const {t} = useTranslation();
    /**
     * A helper for checking whether all required permissions are granted.
     */
    const hasAllPermissions = useHasAllPermissions();
    /**
     * Can be used to check if running on the enterprise edition.
     */
    const enterpriseEditionContext = useEnterpriseEditionContext();

    /**
     * Determines if all required features are available within the active license.
     */
    const hasRequiredFeatures = useCallback((required: ReadonlyArray<CipherMailFeature> = []) =>
    {
        if (!enterpriseEditionContext.enterpriseEdition) {
            // if not running on the enterprise edition, all features are available
            return true;
        }

        if (activeLicense?.features == undefined) {
            return false;
        }

        const features = Array.from(activeLicense.features);

        // the "All" feature is special and always returns true
        if (features.includes(CipherMailFeature.All)) {
            return true;
        }

        return required.every((feature) => feature !== undefined && features.includes(feature));
    }, [activeLicense?.features,
        enterpriseEditionContext.enterpriseEdition]);

    useEffect(() =>
    {
        setForceReload(false);

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        systemApi.systemControllerVersionString()
            .then(response => {
                setVersionString(response.data);
            })
            .catch(() => {
                setVersionString(undefined);
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            });
    }, [forceReload,
        setSpinnerStateCounter
    ]);

    useEffect(() =>
    {
        if (!hasAllPermissions(["tls:get-active-certificate"])) {
            return;
        }

        setForceReload(false);

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        tlsApi.tLSControllerGetActiveCertificate()
            .then(response => {
                setActiveTLSCertificate(response.data);
            })
            .catch(() => {
                setActiveTLSCertificate(undefined);
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            });
    }, [forceReload,
        setSpinnerStateCounter,
        hasAllPermissions
    ]);

    useEffect(() =>
    {
        if (!hasAllPermissions(["license:get-active-license"])) {
            return;
        }

        setForceReload(false);

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        licenseApi.licenseControllerGetActiveLicense(true)
            .then(response => {
                setActiveLicense(response.data);
            })
            .catch(() => {
                setActiveLicense(undefined);
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            });
    }, [forceReload,
        setSpinnerStateCounter,
        hasAllPermissions
    ]);

    useEffect(() =>
    {
        if (!hasAllPermissions(["license:get-available-user-licenses"])) {
            return;
        }

        setForceReload(false);

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        licenseApi.licenseControllerGetAvailableUserLicenses()
            .then(response => {
                setAvailableUserLicenses(response.data);
            })
            .catch(() => {
                setAvailableUserLicenses(undefined);
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            });
    }, [forceReload,
        setSpinnerStateCounter,
        hasAllPermissions
    ]);

    useEffect(() =>
    {
        if (!hasAllPermissions(["mail-queue:get-queue-size"])) {
            return;
        }

        setForceReload(false);

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        mailQueueApi.mailQueueControllerGetQueueSize("outgoing")
            .then(response => {
                setMpaOutgoingQueueSize(response.data);
            })
            .catch(() => {
                setMpaOutgoingQueueSize(undefined);
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            });
    }, [forceReload,
        setSpinnerStateCounter,
        hasAllPermissions
    ]);

    useEffect(() =>
    {
        if (!hasAllPermissions(["mail-queue:get-queue-size"])) {
            return;
        }

        setForceReload(false);

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        mailQueueApi.mailQueueControllerGetQueueSize("spool")
            .then(response => {
                setMpaSpoolQueueSize(response.data);
            })
            .catch(() => {
                setMpaSpoolQueueSize(undefined);
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            });
    }, [forceReload,
        setSpinnerStateCounter,
        hasAllPermissions
    ]);

    useEffect(() =>
    {
        if (!hasAllPermissions(["mail-repository:get-size"])) {
            return;
        }

        setForceReload(false);

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        mailRepositoryApi.mailRepositoryControllerGetMailRepositorySize("file://var/mail/error")
            .then(response => {
                setMpaErrorQueueSize(response.data);
            })
            .catch(() => {
                setMpaErrorQueueSize(undefined);
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            });
    }, [forceReload,
        setSpinnerStateCounter,
        hasAllPermissions
    ]);

    useEffect(() =>
    {
        if (!hasAllPermissions(["mta:get-queue-size"])) {
            return;
        }

        setForceReload(false);

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        mtaApi.mTAControllerGetQueueSize()
            .then(response => {
                setMtaQueueSize(response.data);
            })
            .catch(() => {
                setMtaQueueSize(undefined);
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            });
    }, [forceReload,
        setSpinnerStateCounter,
        hasAllPermissions
    ]);

    useEffect(() =>
    {
        if (!hasAllPermissions(["sms:get-sms-queue-size"])) {
            return;
        }

        setForceReload(false);

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        smsApi.sMSControllerGetSMSQueueSize()
            .then(response => {
                setSmsQueueSize(response.data);
            })
            .catch(() => {
                setSmsQueueSize(undefined);
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            });
    }, [forceReload,
        setSpinnerStateCounter,
        hasAllPermissions
    ]);

    useEffect(() =>
    {
        if (!hasAllPermissions(["quarantine:get-quarantined-mail-count"])) {
            return;
        }

        setForceReload(false);

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        quarantineApi.quarantineControllerGetQuarantinedMailCount()
            .then(response => {
                setQuarantineQueueSize(response.data);
            })
            .catch(() => {
                setQuarantineQueueSize(undefined);
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            });
    }, [forceReload,
        setSpinnerStateCounter,
        hasAllPermissions
    ]);

    useEffect(() =>
    {
        if (!hasAllPermissions(["certificate:get-certificates-count"])) {
            return;
        }

        setForceReload(false);

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        certificateApi.certificateControllerGetCertificatesCount(CertificateStore.Certificates, Expired.All,
                    MissingKeyAlias.Allowed)
            .then(response => {
                setCertificateCount(response.data);
            })
            .catch(() => {
                setCertificateCount(undefined);
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            });
    }, [forceReload,
        setSpinnerStateCounter,
        hasAllPermissions
    ]);

    useEffect(() =>
    {
        if (!hasAllPermissions(["certificate:get-certificates-count"])) {
            return;
        }

        setForceReload(false);

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        certificateApi.certificateControllerGetCertificatesCount(CertificateStore.Certificates, Expired.ExpiredOnly,
                    MissingKeyAlias.Allowed)
            .then(response => {
                setExpiredCertificateCount(response.data);
            })
            .catch(() => {
                setExpiredCertificateCount(undefined);
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            });
    }, [forceReload,
        setSpinnerStateCounter,
        hasAllPermissions
    ]);

    useEffect(() =>
    {
        if (!hasAllPermissions(["certificate:get-certificates-count"])) {
            return;
        }

        setForceReload(false);

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        certificateApi.certificateControllerGetCertificatesCount(CertificateStore.Roots, Expired.All,
            MissingKeyAlias.Allowed)
            .then(response => {
                setRootCount(response.data);
            })
            .catch(() => {
                setRootCount(undefined);
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            });
    }, [forceReload,
        setSpinnerStateCounter,
        hasAllPermissions
    ]);

    useEffect(() =>
    {
        if (!hasAllPermissions(["certificate:get-certificates-count"])) {
            return;
        }

        setForceReload(false);

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        certificateApi.certificateControllerGetCertificatesCount(CertificateStore.Roots, Expired.ExpiredOnly,
            MissingKeyAlias.Allowed)
            .then(response => {
                setExpiredRootCount(response.data);
            })
            .catch(() => {
                setExpiredRootCount(undefined);
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            });
    }, [forceReload,
        setSpinnerStateCounter,
        hasAllPermissions
    ]);

    useEffect(() =>
    {
        if (!hasAllPermissions(["certificate:get-certificates-count"])) {
            return;
        }

        setForceReload(false);

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        certificateApi.certificateControllerGetCertificatesCount(CertificateStore.Certificates, Expired.All,
                    MissingKeyAlias.NotAllowed)
            .then(response => {
                setPrivateKeysCount(response.data);
            })
            .catch(() => {
                setPrivateKeysCount(undefined);
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            });
    }, [forceReload,
        setSpinnerStateCounter,
        hasAllPermissions
    ]);

    useEffect(() =>
    {
        if (!hasAllPermissions(["ca:get-pending-requests-count"])) {
            return;
        }

        setForceReload(false);

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        caApi.cAControllerGetPendingRequestsCount()
            .then(response => {
                setPendingRequestsCount(response.data);
            })
            .catch(() => {
                setPendingRequestsCount(undefined);
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            });
    }, [forceReload,
        setSpinnerStateCounter,
        hasAllPermissions
    ]);

    useEffect(() =>
    {
        if (!hasAllPermissions(["crl:get-crls-count"])) {
            return;
        }

        setForceReload(false);

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        crlApi.cRLControllerGetCRLsCount()
            .then(response => {
                setCrlCount(response.data);
            })
            .catch(() => {
                setCrlCount(undefined);
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            });
    }, [forceReload,
        setSpinnerStateCounter,
        hasAllPermissions
    ]);

    useEffect(() =>
    {
        if (!hasAllPermissions(["pgp-keyring:get-keys-count"])) {
            return;
        }

        setForceReload(false);

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        pgpKeyringApi.pGPKeyringControllerGetKeysCount(Expired.All, MissingKeyAlias.Allowed, PGPKeyType.MasterKey)
            .then(response => {
                setPgpKeyCount(response.data)
            })
            .catch(() => {
                setPgpKeyCount(undefined);
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            });
    }, [forceReload,
        setSpinnerStateCounter,
        hasAllPermissions
    ]);

    useEffect(() =>
    {
        if (!hasAllPermissions(["pgp-keyring:get-keys-count"])) {
            return;
        }

        setForceReload(false);

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        pgpKeyringApi.pGPKeyringControllerGetKeysCount(Expired.ExpiredOnly, MissingKeyAlias.Allowed, PGPKeyType.MasterKey)
            .then(response => {
                setPgpExpiredKeyCount(response.data)
            })
            .catch(() => {
                setPgpExpiredKeyCount(undefined);
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            });
    }, [forceReload,
        setSpinnerStateCounter,
        hasAllPermissions
    ]);

    useEffect(() =>
    {
        if (!hasAllPermissions(["pgp-keyring:get-keys-count"])) {
            return;
        }

        setForceReload(false);

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        pgpKeyringApi.pGPKeyringControllerGetKeysCount(Expired.All, MissingKeyAlias.NotAllowed, PGPKeyType.MasterKey)
            .then(response => {
                setPgpSecretKeyCount(response.data)
            })
            .catch(() => {
                setPgpSecretKeyCount(undefined);
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            });
    }, [forceReload,
        setSpinnerStateCounter,
        hasAllPermissions
    ]);

    useEffect(() =>
    {
        if (!hasAllPermissions(["system:is-service-running"])) {
            return;
        }

        setForceReload(false);

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        systemApi.systemControllerIsServiceRunning(SystemService.PortalBackend)
            .then(response => {
                setPortalBackendRunning(response.data);
            })
            .catch(() => {
                setPortalBackendRunning(undefined);
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            });
    }, [forceReload,
        setSpinnerStateCounter,
        hasAllPermissions
    ]);

    useEffect(() =>
    {
        if (!hasAllPermissions(["system:is-service-running"])) {
            return;
        }

        setForceReload(false);

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        systemApi.systemControllerIsServiceRunning(SystemService.Mta)
            .then(response => {
                setMtaRunning(response.data);
            })
            .catch(() => {
                setMtaRunning(undefined);
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            });
    }, [forceReload,
        setSpinnerStateCounter,
        hasAllPermissions
    ]);

    useEffect(() =>
    {
        if (!hasAllPermissions(["system:is-service-running"])) {
            return;
        }

        setForceReload(false);

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        systemApi.systemControllerIsServiceRunning(SystemService.Imap)
            .then(response => {
                setImapRunning(response.data);
            })
            .catch(() => {
                setImapRunning(undefined);
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            });
    }, [forceReload,
        setSpinnerStateCounter,
        hasAllPermissions
    ]);

    useEffect(() =>
    {
        if (!hasAllPermissions(["user:get-users-count"])) {
            return;
        }

        setForceReload(false);

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        userApi.userControllerGetUsersCount()
            .then(response => {
                setUserCount(response.data);
            })
            .catch(() => {
                setUserCount(undefined);
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            });
    }, [forceReload,
        setSpinnerStateCounter,
        hasAllPermissions
    ]);

    useEffect(() =>
    {
        if (!hasAllPermissions(["domain:get-domains-count"])) {
            return;
        }

        setForceReload(false);

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        domainApi.domainControllerGetDomainCount()
            .then(response => {
                setDomainCount(response.data);
            })
            .catch(() => {
                setDomainCount(undefined);
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            });
    }, [forceReload,
        setSpinnerStateCounter,
        hasAllPermissions
    ]);

    useEffect(() =>
    {
        if (!hasAllPermissions(["auth:get-admins"])) {
            return;
        }

        setForceReload(false);

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        authApi.authControllerGetAdmins()
            .then(response => {
                setAdminCount(response.data.length);
            })
            .catch(() => {
                setAdminCount(undefined);
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            });
    }, [forceReload,
        setSpinnerStateCounter,
        hasAllPermissions
    ]);

    useEffect(() =>
    {
        if (!hasAllPermissions(["system:updates-available"])) {
            return;
        }

        setForceReload(false);

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        systemApi.systemControllerUpdatesAvailable()
            .then(response => {
                setUpdatesAvailable(response.data);
            })
            .catch(() => {
                setUpdatesAvailable(undefined);
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            });
    }, [forceReload,
        setSpinnerStateCounter,
        hasAllPermissions
    ]);

    /**
     * Determines the CSS class for the available user licenses based on the percentage of licenses in use.
     *
     * This function calculates the percentage of licenses currently in use by comparing the number of available user
     * licenses to the maximum number of users allowed by the active license. Based on the percentage, it returns
     * the appropriate CSS class to visually represent the remaining license availability:
     * - "text-success" if less than 50% of licenses are in use.
     * - "text-warning" if 50% to less than 75% of licenses are in use.
     * - "text-danger" if 75% or more of licenses are in use.
     */
    const availableLicensesCss = () =>
    {
        if (activeLicense == undefined || availableUserLicenses === undefined) {
            return undefined;
        }

        const percentageInUse = (activeLicense.maxUsers - availableUserLicenses) / (activeLicense.maxUsers);

        if (percentageInUse < 0.5) {
            return "text-success"
        }
        else if (percentageInUse < 0.75) {
            return "text-warning"
        }
        else {
            return "text-danger"
        }
    }

    const mtaQueueCss = () =>
    {
        if (mtaQueueSize == undefined) {
            return undefined;
        }

        if (mtaQueueSize < 25) {
            return "text-success"
        }
        else if (mtaQueueSize < 100) {
            return "text-warning"
        }
        else {
            return "text-danger"
        }
    }

    const renderMetrics = () =>
    {
        if (enterpriseEditionContext.enterpriseEdition && activeLicense?.features == undefined) {
            return false;
        }

        return (
            <>
                <Row className="mt-2">
                    <h2 className="mb-4">{t('dashboard.metrics')}</h2>

                    <CardGroup>
                        <Card>
                            <Card.Body className="bg-body-secondary">
                                <div>
                                    <div>{t('dashboard.queues-mta')}</div>
                                    <div className={"fs-1 " + mtaQueueCss()}>{mtaQueueSize}</div>
                                </div>
                            </Card.Body>
                        </Card>

                        <Card>
                            <Card.Body className="bg-body-secondary">
                                <div>
                                    <div>{t('dashboard.queues-mpa-error')}</div>
                                    <div className={"fs-1 " + ((mpaErrorQueueSize??0) === 0 ? "text-success" : "text-danger")}>{mpaErrorQueueSize}</div>
                                </div>
                            </Card.Body>
                        </Card>

                        {hasRequiredFeatures([CipherMailFeature.Sms]) &&
                            <Card>
                                <Card.Body className="bg-body-secondary">
                                    <div>
                                        <div>{t('dashboard.queues-sms')}</div>
                                        <div className="text-success fs-1">{smsQueueSize}</div>
                                    </div>
                                </Card.Body>
                            </Card>
                        }

                        {hasRequiredFeatures([CipherMailFeature.Smime]) &&
                            <Card>
                                <Card.Body className="bg-body-secondary">
                                    <div>
                                        <div>{t('dashboard.smime-pending-requests')}</div>
                                        <div className="text-success fs-1">{pendingRequestsCount}</div>
                                    </div>
                                </Card.Body>
                            </Card>
                        }

                        {hasRequiredFeatures([CipherMailFeature.Dlp]) &&
                            <Card>
                                <Card.Body className="bg-body-secondary">
                                    <div>
                                        <div>{t('dashboard.queues-quarantine')}</div>
                                        <div className={"fs-1 " + ((quarantineQueueSize??0) === 0 ? "text-success" : "text-danger")}>{quarantineQueueSize}</div>
                                    </div>
                                </Card.Body>
                            </Card>
                        }

                        {hasRequiredFeatures([CipherMailFeature.Smime]) &&
                            <Card>
                                <Card.Body className="bg-body-secondary">
                                    <div>
                                        <div>{t('dashboard.smime-expired-roots')}</div>
                                        <div className="text-success fs-1">{expiredRootCount}</div>
                                    </div>
                                </Card.Body>
                            </Card>
                        }

                        {(activeLicense?.maxUsers??0) > 0 &&
                            <Card>
                                <Card.Body className="bg-body-secondary">
                                    <div>
                                        <div>{t('dashboard.available-licenses')}</div>
                                        <div className={"fs-1 " + availableLicensesCss()}>{availableUserLicenses}</div>
                                    </div>
                                </Card.Body>
                            </Card>
                        }
                    </CardGroup>
                </Row>

                <Row className="mt-4">
                    <CardGroup>
                        <Card className="me-2">
                            <Card.Header><QueuesIcon className="text-success"/> {t('dashboard.queues')}</Card.Header>
                            <Card.Body>
                                <div className="hstack gap-4 align-items-start">
                                    <DashboardTable>
                                        <DashboardTableRow name={t('dashboard.queues-mta')} value={mtaQueueSize} linkProps={{to: "/mta/queue/mta"}}/>
                                        <DashboardTableRow name={t('dashboard.queues-mpa-outgoing')} value={mpaOutgoingQueueSize} linkProps={{to: "/mta/queue/mpa", state: {selectedQueueName: "outgoing"}}}/>
                                        <DashboardTableRow name={t('dashboard.queues-mpa-spool')} value={mpaSpoolQueueSize} linkProps={{to: "/mta/queue/mpa", state: {selectedQueueName: "spool"}}}/>
                                    </DashboardTable>

                                    <DashboardTable>
                                        <DashboardTableRow name={t('dashboard.queues-mpa-error')} value={mpaErrorQueueSize} linkProps={{to: "/mta/queue/repository"}}/>
                                        {hasRequiredFeatures([CipherMailFeature.Sms]) &&
                                            <DashboardTableRow name={t('dashboard.queues-sms')} value={smsQueueSize} linkProps={{to: "/sms"}}/>
                                        }
                                        {hasRequiredFeatures([CipherMailFeature.Dlp]) &&
                                            <DashboardTableRow name={t('dashboard.queues-quarantine')} value={quarantineQueueSize} linkProps={{to: "/dlp/quarantine"}}/>
                                        }
                                    </DashboardTable>
                                </div>
                            </Card.Body>
                        </Card>

                        {hasRequiredFeatures([CipherMailFeature.Smime]) &&
                            <Card className="ms-2 border-start">
                                <Card.Header><SMIMEIcon className="text-success"/> {t('dashboard.smime')}</Card.Header>
                                <Card.Body>
                                    <div className="hstack gap-4 align-items-start">
                                        <DashboardTable>
                                            <DashboardTableRow name={t('dashboard.smime-certificates')} value={certificateCount} linkProps={{to: "/certificates"}}/>
                                            <DashboardTableRow name={t('dashboard.smime-expired-certificates')} value={expiredCertificateCount} linkProps={{to: "/certificates", state: {expired: Expired.ExpiredOnly}}}/>
                                            <DashboardTableRow name={t('dashboard.smime-roots')} value={rootCount} linkProps={{to: "/certificates", state: {certificateStore: CertificateStore.Roots}}}/>
                                            <DashboardTableRow name={t('dashboard.smime-expired-roots')} value={expiredRootCount} linkProps={{to: "/certificates", state: {certificateStore: CertificateStore.Roots, expired: Expired.ExpiredOnly}}}/>
                                        </DashboardTable>

                                        <DashboardTable>
                                            <DashboardTableRow name={t('dashboard.smime-private-keys')} value={privateKeysCount} linkProps={{to: "/certificates", state: {missingKeyAlias: MissingKeyAlias.NotAllowed}}}/>
                                            <DashboardTableRow name={t('dashboard.smime-pending-requests')} value={pendingRequestsCount} linkProps={{to: "/ca"}}/>
                                            <DashboardTableRow name={t('dashboard.smime-crls')} value={crlCount} linkProps={{to: "/crls"}}/>
                                        </DashboardTable>
                                    </div>
                                </Card.Body>
                            </Card>
                        }
                    </CardGroup>
                </Row>

                <Row className="mt-4">
                    <CardGroup>
                        {hasRequiredFeatures([CipherMailFeature.Pgp]) &&
                            <Card className="me-2">
                                <Card.Header><PGPIcon className="text-success"/> {t('dashboard.pgp')}</Card.Header>
                                <Card.Body>
                                    <div className="hstack gap-4 align-items-start">
                                        <DashboardTable>
                                            <DashboardTableRow name={t('dashboard.pgp-keys')} value={pgpKeyCount} linkProps={{to: "/pgp"}}/>
                                            <DashboardTableRow name={t('dashboard.pgp-expired-keys')} value={pgpExpiredKeyCount} linkProps={{to: "/pgp", state: {expired: Expired.ExpiredOnly}}}/>
                                            <DashboardTableRow name={t('dashboard.pgp-secret-keys')} value={pgpSecretKeyCount} linkProps={{to: "/pgp", state: {missingKeyAlias: MissingKeyAlias.NotAllowed}}}/>
                                        </DashboardTable>
                                    </div>
                                </Card.Body>
                            </Card>
                        }

                        <Card className="me-2 ms-2 border-start">
                            <Card.Header><OtherIcon className="text-success"/> {t('dashboard.other')}</Card.Header>
                            <Card.Body>
                                <div className="hstack gap-4 align-items-start">
                                    <DashboardTable>
                                        <DashboardTableRow name={t('dashboard.other-users')} value={userCount} linkProps={{to: "/users"}}/>
                                        <DashboardTableRow name={t('dashboard.other-domains')} value={domainCount} linkProps={{to: "/domains"}}/>
                                        <DashboardTableRow name={t('dashboard.other-admins')} value={adminCount}/>
                                    </DashboardTable>
                                </div>
                            </Card.Body>
                        </Card>

                        <Card className="ms-2 border-start">
                            <Card.Header><ServicesIcon className="text-success"/> {t('dashboard.services')}</Card.Header>
                            <Card.Body>
                                <div className="hstack gap-4 align-items-start">
                                    <DashboardTable>
                                        <DashboardTableRow name={t('dashboard.services-portal-backend')} value={portalBackendRunning ? t('dashboard.running') : t('dashboard.not-running')}
                                                           className={portalBackendRunning ? "text-success" : "text-danger"}/>
                                        <DashboardTableRow name={t('dashboard.services-mta')} value={mtaRunning ? t('dashboard.running') : t('dashboard.not-running')}
                                                           className={mtaRunning ? "text-success" : "text-danger"}/>

                                        {enterpriseEditionContext.enterpriseEdition && hasRequiredFeatures([CipherMailFeature.Webmail]) &&
                                            <DashboardTableRow name={t('dashboard.services-imap')} value={imapRunning ? t('dashboard.running') : t('dashboard.not-running')}
                                                               className={imapRunning ? "text-success" : "text-danger"}/>
                                        }
                                    </DashboardTable>
                                </div>
                            </Card.Body>
                        </Card>
                    </CardGroup>
                </Row>
            </>
        )
    }

    const availableLicenses = () =>
    {
        if (activeLicense?.maxUsers === undefined) {
            return 0;
        }

        return activeLicense?.maxUsers === 0 ? t('dashboard.unlimited-licenses') : <Link to="/licenses" state={{showManageLicensedUsersPopup: true}}>{availableUserLicenses  + "/" + activeLicense?.maxUsers}</Link>;
    }

    return (
        <>
            <Row>
                <div className="d-flex flex-row align-items-center border-bottom pb-2 mb-4">
                    <div className="d-flex justify-content-start">
                        <PageTitle title={t("dashboard.title")} icon={<DashboardIcon />}/>
                    </div>
                    <div className="d-flex flex-fill justify-content-end">
                        <ReloadButton onClickReload={() => setForceReload(true)} />
                    </div>
                </div>
            </Row>

            <Row>
                {
                    updatesAvailable !== undefined && updatesAvailable &&
                    <div>
                        <RenderAlert variant="danger" message={t('dashboard.updates-available')}/>
                    </div>
                }
            </Row>

            <div className="row overflow-auto">
                <Row className="mt-2">
                    <h2 className="mb-4">{t('dashboard.system-info')}</h2>
                    <CardGroup>
                        <Card className="me-2">
                            <Card.Header><TlsIcon className="text-success"/> {t('dashboard.tls-certificate')}</Card.Header>
                            <Card.Body>
                                <Table hover responsive size="sm">
                                    <colgroup>
                                        <col className="col-md-2"/>
                                        <col className="col-md-10"/>
                                    </colgroup>
                                    <tbody>
                                    <tr>
                                        <td>{t('certificate-table-header.subject')}</td>
                                        <td className={"user-select-all"}>{activeTLSCertificate?.subjectFriendly}</td>
                                    </tr>
                                    <tr>
                                        <td>{t('certificate-table-header.issuer')}</td>
                                        <td className={"user-select-all"}>{activeTLSCertificate?.issuerFriendly}</td>
                                    </tr>
                                    <tr>
                                        <td>{t('certificate-table-header.serial-number')}</td>
                                        <td className={"user-select-all"}>{activeTLSCertificate?.serialNumberHex}</td>
                                    </tr>
                                    <tr>
                                        <td>{t('certificate-table-header.not-after')}</td>
                                        <td className={"user-select-all"}>{DateUtils.timestampToUTCString(activeTLSCertificate?.notAfter)}</td>
                                    </tr>
                                    </tbody>
                                </Table>
                            </Card.Body>
                        </Card>

                        <Card className="ms-2 border-start">
                            <Card.Header><LicensesIcon className="text-success"/> {t('dashboard.license')}</Card.Header>
                            <Card.Body>
                                <Table hover responsive size="sm">
                                    <colgroup>
                                        <col className="col-md-2"/>
                                        <col className="col-md-10"/>
                                    </colgroup>
                                    <tbody>
                                    {enterpriseEditionContext.enterpriseEdition &&
                                        <>
                                            <tr>
                                                <td>{t('licenses.company')}</td>
                                                <td className={"user-select-all"}>{activeLicense?.company}</td>
                                            </tr>
                                            <tr>
                                                <td>{t('licenses.expiration-date')}</td>
                                                <td className={"user-select-all"}>{DateUtils.timestampToUTCString(activeLicense?.expirationDate)}</td>
                                            </tr>
                                            <tr>
                                                <td>{t('licenses.comment')}</td>
                                                <td className={"user-select-all"}>{activeLicense?.comments}</td>
                                            </tr>
                                            <tr>
                                                <td>{t('licenses.valid')}</td>
                                                <td className={activeLicense?.valid ? "text-success" : "text-danger"}>{activeLicense?.valid ? t('common.yes') : t('common.no')}</td>
                                            </tr>
                                            <tr>
                                                <td>{t('licenses.features')}</td>
                                                <td className={"user-select-all"}>{Array.from(activeLicense?.features??[]).join(", ")}</td>
                                            </tr>
                                            <tr>
                                                <td>{t('dashboard.available-licenses')}</td>
                                                <td className={"user-select-all"}>{availableLicenses()}</td>
                                            </tr>
                                        </>
                                    }
                                    <tr>
                                        <td>{t('dashboard.version')}</td>
                                        <td className={"user-select-all"}>{versionString}</td>
                                    </tr>
                                    </tbody>
                                </Table>
                            </Card.Body>
                        </Card>
                    </CardGroup>
                </Row>

                {renderMetrics()}
            </div>
        </>
    )
}